import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Flex, message, notification, Typography, Upload, Modal, Table } from 'antd';
import { Card, CardBody, Container } from 'reactstrap';
import { Col, Row } from 'react-bootstrap';
import Palette from '../../../utils/Palette';
import Iconify from '../../reusable/Iconify';
import AuthorModel from '../../../models/AuthorModel';
import BookModel from '../../../models/BookModel';
import PublisherModel from '../../../models/PublisherModel';
import NewsModel from '../../../models/NewsModel';
import EBulletinModel from '../../../models/EBulletinModel';
import MediaModel from '../../../models/MediaModel';
import IllustratorModel from '../../../models/IllustratorModel';
import TranslatorModel from '../../../models/TranslatorModel';
import LiteraryAgenciesModel from '../../../models/LiteraryAgenciesModel';
import CategoryModel from '../../../models/CategoryModel';
import SubCategoryModel from '../../../models/SubCategoryModel';
import PartnerModel from '../../../models/PartnerModel';
import BannerModel from '../../../models/BannerModel';
import bulkUploadConfigs from '../../../config/bulkUploadConfigs';

const { Dragger } = Upload;
const MAX_FILE_SIZE_MB = 10;

const ENTITY_MODEL_MAP = {
  author: AuthorModel,
  book: BookModel,
  publisher: PublisherModel,
  news: NewsModel,
  eBulletin: EBulletinModel,
  media: MediaModel,
  illustrator: IllustratorModel,
  translator: TranslatorModel,
  literaryAgency: LiteraryAgenciesModel,
  category: CategoryModel,
  subCategory: SubCategoryModel,
  partner: PartnerModel,
  banner: BannerModel,
};

const formatRowNumbers = (rows) => {
  const shown = rows.slice(0, 3).join(', ');
  const rest = rows.length - 3;
  return rest > 0 ? `${shown} (+${rest} baris lain)` : shown;
};

const buildIssueLines = (invalidRows, fieldLabels, fieldFixes) => {
  const byField = new Map();
  for (const row of invalidRows) {
    for (const err of row.errors || []) {
      const field = (err.match(/^"([^"]+)"/) || [])[1] || '_other';
      if (!byField.has(field)) {
        byField.set(field, { rows: [], raw: err });
      }
      const group = byField.get(field);
      group.rows.push(row.row_number);
    }
  }

  const lines = [];
  const fieldEntries = Array.from(byField.entries()).slice(0, 5);
  for (const [field, group] of fieldEntries) {
    const rowsText = formatRowNumbers(group.rows);
    let line;
    if (fieldLabels[field]) {
      line = `Terdapat kesalahan pada ${fieldLabels[field]} di baris ${rowsText}. ${fieldFixes[field] || ''}`;
    } else {
      line = `Terdapat kesalahan di baris ${rowsText}: ${group.raw}`;
    }
    lines.push(line);
  }
  const remainingFields = byField.size - fieldEntries.length;
  if (remainingFields > 0) {
    lines.push(`…dan ${remainingFields} jenis kesalahan lainnya.`);
  }
  return lines;
};

const showIssueNotification = (result, title, fieldLabels, fieldFixes) => {
  const invalidRows = (result?.rows || []).filter((row) => !row.valid);
  if (invalidRows.length === 0) return;
  const lines = buildIssueLines(invalidRows, fieldLabels, fieldFixes);
  notification.warning({
    message: `${title} — ${invalidRows.length} baris tidak valid`,
    description: (
      <div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {lines.map((line, idx) => (
            <li key={idx} style={{ marginBottom: 4 }}>{line}</li>
          ))}
        </ul>
      </div>
    ),
    duration: 0,
  });
};

const getEntityKeyFromPathname = (pathname) => {
  const segments = (pathname || '').split('/').filter(Boolean);
  const entitySlug = segments[0];
  const slugToKey = {
    authors: 'author',
    books: 'book',
    publishers: 'publisher',
    news: 'news',
    medias: 'media',
    illustrators: 'illustrator',
    translators: 'translator',
    'literary-agencies': 'literaryAgency',
    categories: 'category',
    'sub-categories': 'subCategory',
  };
  return slugToKey[entitySlug] || null;
};

export default function BulkUploadPage(props) {
  const history = useHistory();
  const { location } = props;
  const entityKey = getEntityKeyFromPathname(location.pathname);
  const config = entityKey ? bulkUploadConfigs[entityKey] : null;

  const [templateLoading, setTemplateLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [errorRows, setErrorRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  if (!config) {
    return null;
  }

  const Model = ENTITY_MODEL_MAP[entityKey];
  const { fieldLabels, fieldFixes } = config;

  const downloadErrorReport = () => {
    if (errorRows.length === 0) return;
    const header = 'row_number,field,error\n';
    const csv = errorRows
      .map((row) =>
        row.errors.map(
          (err) => `"${row.row_number}","${err.replace(/"/g, '""')}"`
        )
      )
      .flat()
      .join('\n');
    const blob = new Blob([header + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${entityKey}_bulk_upload_error_report.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      await Model.downloadTemplate();
      message.success('Template berhasil didownload');
    } catch (e) {
      message.error(e?.message || 'Gagal mendownload template');
    } finally {
      setTemplateLoading(false);
    }
  };

  const runDryRun = async (file) => {
    setPreviewLoading(true);
    setPreview(null);
    setImportResult(null);
    try {
      const result = await Model.bulkUpload(file, true);
      setPreview(result);
      if (result.valid_rows > 0) {
        message.success(`${result.valid_rows} baris valid dan siap diimport.`);
      }
    } catch (e) {
      message.error(e?.error_message || e?.message || 'Gagal memvalidasi file');
    } finally {
      setPreviewLoading(false);
    }
  };

  const beforeUpload = (file) => {
    const isXlsx = file.name?.toLowerCase().endsWith('.xlsx');
    if (!isXlsx) {
      message.error('Hanya file Excel (.xlsx) yang diizinkan.');
      return Upload.LIST_IGNORE;
    }
    const isLt10M = file.size / 1024 / 1024 <= MAX_FILE_SIZE_MB;
    if (!isLt10M) {
      message.error(`Ukuran file harus lebih kecil dari ${MAX_FILE_SIZE_MB}MB.`);
      return Upload.LIST_IGNORE;
    }
    setFileList([{
      uid: '-1',
      name: file.name,
      status: 'done',
      size: file.size,
      type: file.type,
      originFileObj: file,
    }]);
    setSelectedFile(file);
    runDryRun(file);
    return false;
  };

  const handleRemoveFile = () => {
    setFileList([]);
    setSelectedFile(null);
    setPreview(null);
    setImportResult(null);
  };

  const handleImport = () => {
    if (!preview || preview.valid_rows === 0) return;
    Modal.confirm({
      title: `Import ${config.label}?`,
      content: `${preview.valid_rows} baris valid akan dibuat menjadi ${config.label.toLowerCase()}.${preview.invalid_rows > 0 ? ` ${preview.invalid_rows} baris tidak valid akan dilewati.` : ''}`,
      okText: 'Import',
      onOk: async () => {
        setImporting(true);
        try {
          const result = await Model.bulkUpload(selectedFile, false);
          setImportResult(result);
          if (result.created > 0) {
            message.success(`Berhasil mengimport ${preview.valid_rows} ${config.label}.`);
          }
          if (result.invalid_rows > 0) {
            showIssueNotification(result, 'Sebagian baris gagal diimport karena ada kesalahan', fieldLabels, fieldFixes);
          }
          if (result.created > 0 && result.invalid_rows === 0) {
            setTimeout(() => history.push(config.backPath), 2000);
          }
        } catch (e) {
          message.error(e?.error_message || e?.message || `Gagal mengimport ${config.label}`);
        } finally {
          setImporting(false);
        }
      },
    });
  };

  const allRows = preview?.rows || [];
  let tableColumns = [];

  const formatCell = (val) => {
    if (val === undefined || val === null || val === '') return '-';
    if (val instanceof Date) return val.toISOString().slice(0, 10);
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  if (allRows.length > 0) {
    const headers =
      preview?.headers ||
      Array.from(new Set(allRows.flatMap((row) => Object.keys(row.raw || {}))));

    tableColumns = [
      {
        title: 'No',
        dataIndex: 'row_number',
        key: 'row_number',
        width: 100,
        fixed: 'left',
      },
      {
        title: 'Status',
        dataIndex: 'valid',
        key: 'status',
        width: 100,
        fixed: 'left',
        render: (isValid) => (
          <span style={{ color: isValid ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
            {isValid ? 'Valid' : 'Invalid'}
          </span>
        ),
      },
      ...headers.map((key) => ({
        title: key,
        key,
        width: 180,
        ellipsis: true,
        render: (_, record) => formatCell(record.raw?.[key]),
      })),
      {
        title: 'Keterangan Error',
        dataIndex: 'errors',
        key: 'errors',
        width: 300,
        fixed: 'right',
        render: (errors) =>
          errors && errors.length > 0 ? (
            <span style={{ color: '#ff4d4f' }}>{errors.join(' ')}</span>
          ) : (
            '-'
          ),
      },
    ];
  }

  return (
    <>
      <Container fluid>
        <Card style={{ background: Palette.BACKGROUND_DARK_GRAY, color: "white" }}
          className="card-stats mb-4 mb-xl-0">
          <CardBody>
            <Row>
              <Col className='mb-3' md={6}>
                <Flex align='center' gap={8}>
                  <Link to={config.backPath}>
                    <Flex align='center'>
                      <Iconify icon={'material-symbols:arrow-back-rounded'} style={{ fontSize: "16px", color: "white" }} />
                    </Flex>
                  </Link>
                  <span style={{ fontWeight: "bold", fontSize: "1.1em" }}>{config.title}</span>
                </Flex>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Typography.Title level={4} style={{ color: "white" }}>Download Template</Typography.Title>
                <div style={{ marginTop: "12px" }}>
                  <Button
                    type="default"
                    icon={<Iconify icon={"material-symbols:download"} style={{ fontSize: "16px" }} />}
                    loading={templateLoading}
                    onClick={handleDownloadTemplate}
                  >
                    Download Template (.xlsx)
                  </Button>
                </div>
              </Col>
            </Row>

            <div style={{ borderTop: "1px solid #444", margin: "24px 0" }} />

            {/* Upload */}
            <Row>
              <Col md={12}>
                <Typography.Title level={4} style={{ color: "white" }}>Upload File (.xlsx)</Typography.Title>
                <div style={{ marginTop: "12px" }}>
                  <Dragger
                    accept=".xlsx"
                    multiple={false}
                    maxCount={1}
                    fileList={fileList}
                    beforeUpload={beforeUpload}
                    onRemove={handleRemoveFile}
                    style={{ background: "#2b2b2b" }}
                  >
                    <p className="ant-upload-drag-icon">
                      <Iconify icon={"material-symbols:upload-file-outline"} style={{ fontSize: "40px", color: "white" }} />
                    </p>
                    <p className="ant-upload-text" style={{ color: "white" }}>Klik atau drag file .xlsx kamu ke sini</p>
                    <p className="ant-upload-hint" style={{ color: "#aaa" }}>Hanya format Excel (.xlsx)</p>
                  </Dragger>
                  {fileList.length > 0 && (
                    <Flex align='center' gap={12} style={{ marginTop: '12px' }}>
                      {previewLoading && (
                        <Typography.Text type="secondary">
                          Sedang memvalidasi isi file...
                        </Typography.Text>
                      )}
                      <Button
                        type="primary"
                        icon={<Iconify icon={"material-symbols:delete-outline"} style={{ fontSize: "16px" }} />}
                        disabled={importing}
                        onClick={handleRemoveFile}
                      >
                        Hapus File
                      </Button>
                    </Flex>
                  )}
                </div>
              </Col>
            </Row>

            {/* Preview Table */}
            {allRows.length > 0 && !previewLoading && (
              <>
                <div style={{ borderTop: "1px solid #444", margin: "24px 0" }} />
                <Row>
                  <Col md={12}>
                    <Typography.Title level={4} style={{ color: "white" }}>
                      Preview Data Excel ({allRows.length} baris)
                    </Typography.Title>
                    <div style={{ marginTop: "12px" }}>
                      <Table
                        className='bulk-preview-table'  
                        dataSource={allRows}
                        columns={tableColumns}
                        rowKey={(record) => record.row_number || Math.random()}
                        pagination={{ pageSize: 5, showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} baris`, showSizeChanger: false}}
                        scroll={{ x: 'max-content' }}
                        size="middle"
                      />
                    </div>
                  </Col>
                </Row>
              </>
            )}

            <div style={{ borderTop: "1px solid #444", margin: "24px 0" }} />

            {/* Import */}
            <Row>
              <Col md={12}>
                <Typography.Title level={4} style={{ color: "white" }}>Import</Typography.Title>
                <Flex gap={12}>
                  <Button
                    type="primary"
                    icon={<Iconify icon={"material-symbols:upload"} style={{ fontSize: "16px" }} />}
                    loading={importing}
                    disabled={!preview || preview.valid_rows === 0 || previewLoading}
                    onClick={handleImport}
                  >
                    {`Import ${preview ? `${preview.valid_rows} Baris Valid` : config.label}`}
                  </Button>
                  <Link to={config.backPath}>
                    <Button type="default">Kembali ke {config.label}</Button>
                  </Link>
                </Flex>

                {importResult && (
                  <div style={{ marginTop: "16px" }}>
                    <Button type="default" onClick={handleRemoveFile}>
                      Upload File Lain
                    </Button>
                  </div>
                )}
              </Col>
            </Row>

          </CardBody>
        </Card>
      </Container>
    </>
  );
}
